import { axiosClient } from '../../../services/axiosClient'

// Base URL for the API - uses REACT_APP_NEW_BASE_URL from .env
const API_BASE_URL = (process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING || 'http://localhost:3003').replace(/\/$/, '')

// Helper function to transform MongoDB data
const transformMongoDBData = (data) => {
  if (Array.isArray(data)) {
    return data.map(transformMongoDBData)
  } else if (data && typeof data === 'object') {
    if (data.$oid) {
      return data.$oid
    }
    if (data.$date) {
      return new Date(data.$date).toISOString()
    }
    if (data.$numberLong) {
      return parseInt(data.$numberLong, 10)
    }
    const transformed = {}
    for (const key in data) {
      transformed[key] = transformMongoDBData(data[key])
    }
    return transformed
  }
  return data
}

// Use centralized axios client directly with baseURL
// Use centralized axios client directly
const apiClient = axiosClient
// REMOVED mutation: apiClient.defaults.baseURL = API_BASE_URL

// Response interceptor to handle MongoDB ObjectId format
apiClient.interceptors.response.use(
  (response) => {
    if (response.data && typeof response.data === 'object') {
      response.data = transformMongoDBData(response.data)
    }
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    throw error
  }
)

// DGR Service Functions

// Fetch all reports with optional filters
export const fetchDGRReports = async (filters = {}) => {
  try {
    const params = new URLSearchParams()

    if (filters.startDate) params.append('startDate', filters.startDate)
    if (filters.endDate) params.append('endDate', filters.endDate)
    if (filters.plantId) params.append('plantId', filters.plantId)
    if (filters.plantName) params.append('plantName', filters.plantName)

    const response = await apiClient.get(API_BASE_URL + '/daily-generation-report', { params })
    return response.data || []
  } catch (error) {
    console.error('Error fetching DGR reports:', error)
    throw error
  }
}

// Fetch single report by ID
export const fetchDGRById = async (dgrId) => {
  try {
    const response = await apiClient.get(API_BASE_URL + `/daily-generation-report/${dgrId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching DGR report:', error)
    throw error
  }
}

// Create new DGR report
export const createDGRReport = async (dgrData) => {
  try {
    const response = await apiClient.post(API_BASE_URL + '/daily-generation-report', dgrData)
    return response.data
  } catch (error) {
    console.error('Error creating DGR report:', error)
    throw error
  }
}

// Update existing DGR report
export const updateDGRReport = async (dgrId, dgrData) => {
  try {
    const response = await apiClient.put(API_BASE_URL + `/daily-generation-report/${dgrId}`, dgrData)
    return response.data
  } catch (error) {
    console.error('Error updating DGR report:', error)
    throw error
  }
}

// Delete DGR report
export const deleteDGRReport = async (dgrId) => {
  try {
    const response = await apiClient.delete(API_BASE_URL + `/daily-generation-report/${dgrId}`)
    return response.data
  } catch (error) {
    console.error('Error deleting DGR report:', error)
    throw error
  }
}

// Fetch plant list for form dropdowns (combined DB + hardcoded plants)
export const fetchPlantListForDGR = async (reportDate) => {
  try {
    const params = {}
    if (reportDate) {
      params.reportDate = reportDate
    }
    const response = await apiClient.get(API_BASE_URL + '/daily-generation-report/form-plants', { params })
    return response.data || []
  } catch (error) {
    console.error('Error fetching plants:', error)
    throw error
  }
}

// Fetch auto-calculated metrics for a specific plant and date
export const fetchPlantMetrics = async (plantId, reportDate) => {
  try {
    const response = await apiClient.get(
      API_BASE_URL + `/daily-generation-report/metrics/${plantId}`,
      { params: { reportDate } }
    )
    const data = response.data || {}

    // Helper to safely convert values (including null/undefined/empty) to numbers (0 if missing)
    const toNumber = (v) => {
      if (v === '' || v === null || v === undefined) return 0
      const n = Number(v)
      return Number.isNaN(n) ? 0 : n
    }

    // Check if it's a hardcoded plant (starts with c0ffee)
    const isHardcoded = plantId.startsWith('c0ffee')

    // If plant is hardcoded, return empty metrics object
    if (isHardcoded) {
      return {
        success: true,
        metrics: {},
        isHardcoded: true,
        message: 'Hardcoded plant - please enter metrics manually',
      }
    }

    // For DB plants, check if metrics are available
    const hasMetrics = data && Object.keys(data).length > 0 &&
      (data.gti !== undefined || data.actYield !== undefined ||
        data.actGeneration !== undefined || data.pr !== undefined)

    if (!hasMetrics) {
      return {
        success: true,
        metrics: {},
        isHardcoded: false,
        message: 'No metrics available for this plant/date - please enter manually',
      }
    }

    // Clean metrics: ensure all fields are numbers and missing values become 0
    const cleanMetrics = {
      gti: toNumber(data.gti),
      actYield: toNumber(data.actYield),
      actGeneration: toNumber(data.actGeneration),
      pr: toNumber(data.pr),
    }

    return {
      success: true,
      metrics: cleanMetrics,
      isHardcoded: false,
      message: 'Metrics fetched from database',
    }
  } catch (error) {
    console.error('Error fetching plant metrics:', error)

    // If it's a hardcoded plant, return empty metrics
    if (plantId.startsWith('c0ffee')) {
      return {
        success: true,
        metrics: {},
        isHardcoded: true,
        message: 'Hardcoded plant - please enter metrics manually',
      }
    }

    return null
  }
}

//DGR import from excel/csv file
export const importDailyGenerationReports = async (file, month) => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('month', month)

    const response = await apiClient.post(
      API_BASE_URL + '/daily-generation-report/import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return response.data
  } catch (error) {
    console.error('Error importing daily generation reports:', error)
    throw error
  }
}

export default {
  fetchDGRReports,
  fetchDGRById,
  createDGRReport,
  updateDGRReport,
  deleteDGRReport,
  fetchPlantListForDGR,
  fetchPlantMetrics,
  importDailyGenerationReports,
}
